/**
 * Symphony GROUP CHAT, Phase 7 — files shared into a group (design D15, D21, D22, D25; §10).
 *
 * Sharing a file into a group grants the group the LIVE ORIGINAL by its file id — never a copy — editable by default
 * (D22), read-only when the owner picks it. This module is the pure half (no React, no fetch): the wire rows, what an
 * answer means, and the gates. `group-files-api.ts` moves the data. iOS `GroupFiles.swift` and Android `GroupFiles.kt`
 * hold the same decisions; `group-files.test.ts` pins this one.
 *
 * Identity is never in a body: every call names the conversation and the file it acts ON, and symphony-bff takes the
 * actor from the session.
 */
import type { GroupActionOutcome } from './group-chat-actions';
import { refusalKey } from './group-chat-actions';
import type { WorkspaceFile } from './workspace-files';

export type GroupFilePermission = 'EDIT' | 'READ';

/** A file shared into a group, as `files` / `my-files` list it. `contentHead` is the HEAD of the body only. */
export interface GroupFile {
  conversationId: string;
  fileId: string;
  ownerMetaSiteId: string | null;
  ownerName: string;
  mine: boolean;
  permission: GroupFilePermission;
  available: boolean;
  /** Phase 7 follow-up: why it cannot be opened (null when available, or the service named no reason). */
  unavailableReason: GroupFileUnavailableReason | null;
  fileName: string;
  contentType: string | null;
  sizeBytes: number | null;
  createdDate: string | null;
  updatedDate: string | null;
  sharedDate: string | null;
  contentHead: string;
  contentTruncated: boolean;
  referenceUrl: string | null;
  hint: string | null;
  etag: string | null;
}

/** One group a file of the caller's is shared into ("Shared with groups" on the file's details). */
export interface GroupFileShare {
  conversationId: string;
  fileId: string;
  fileName: string;
  permission: GroupFilePermission;
  active: boolean;
  sharedDate: string | null;
  revokedDate: string | null;
  lastEditedByMetaSiteId: string | null;
  lastEditedByMaestro: boolean;
  lastEditedDate: string | null;
}

/** `MessageAttachment.groupFile` — the chat card's reference. */
export interface GroupFileRef {
  conversationId: string;
  fileId: string;
  ownerMetaSiteId: string | null;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

/** Absent or unknown reads as EDIT: the proto's unset value IS edit (D22), so an older answer never reads read-only. */
export function parsePermission(v: unknown): GroupFilePermission {
  return v === 'READ' ? 'READ' : 'EDIT';
}

/** Why a listed group file cannot be opened, as the gateway names it. */
export type GroupFileUnavailableReason = 'REVOKED' | 'DELETED' | 'OWNER_GONE' | 'UNREADABLE';
const UNAVAILABLE_REASONS: readonly GroupFileUnavailableReason[] = ['REVOKED', 'DELETED', 'OWNER_GONE', 'UNREADABLE'];
export function parseUnavailableReason(v: unknown): GroupFileUnavailableReason | null {
  return (UNAVAILABLE_REASONS as readonly unknown[]).includes(v) ? (v as GroupFileUnavailableReason) : null;
}
/** The Files list's caption for an unavailable row: the specific reason, or the generic "File unavailable". */
export function unavailableReasonKey(reason: GroupFileUnavailableReason | null): string {
  switch (reason) {
    case 'REVOKED':
      return 'chat.group.files.revokedTitle';
    case 'DELETED':
      return 'chat.group.files.reason.deleted';
    case 'OWNER_GONE':
      return 'chat.group.files.reason.ownerGone';
    case 'UNREADABLE':
      return 'chat.group.files.reason.unreadable';
    default:
      return 'chat.group.files.unavailableTitle';
  }
}

export function parseGroupFile(raw: unknown): GroupFile | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const conversationId = str(r.conversationId);
  const fileId = str(r.fileId);
  if (!conversationId || !fileId) return null;
  return {
    conversationId,
    fileId,
    ownerMetaSiteId: str(r.ownerMetaSiteId),
    ownerName: typeof r.ownerName === 'string' ? r.ownerName : '',
    mine: r.mine === true,
    permission: parsePermission(r.permission),
    available: r.available === true,
    unavailableReason: r.available === true ? null : parseUnavailableReason(r.unavailableReason),
    fileName: typeof r.fileName === 'string' ? r.fileName : '',
    contentType: str(r.contentType),
    sizeBytes: typeof r.sizeBytes === 'number' && Number.isFinite(r.sizeBytes) ? r.sizeBytes : null,
    createdDate: str(r.createdDate),
    updatedDate: str(r.updatedDate),
    sharedDate: str(r.sharedDate),
    contentHead: typeof r.contentHead === 'string' ? r.contentHead : '',
    contentTruncated: r.contentTruncated === true,
    referenceUrl: str(r.referenceUrl),
    hint: str(r.hint),
    etag: str(r.etag),
  };
}

export function parseGroupFileShare(raw: unknown): GroupFileShare | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const conversationId = str(r.conversationId);
  const fileId = str(r.fileId);
  if (!conversationId || !fileId) return null;
  return {
    conversationId,
    fileId,
    fileName: typeof r.fileName === 'string' ? r.fileName : '',
    permission: parsePermission(r.permission),
    active: r.active === true,
    sharedDate: str(r.sharedDate),
    revokedDate: str(r.revokedDate),
    lastEditedByMetaSiteId: str(r.lastEditedByMetaSiteId),
    lastEditedByMaestro: r.lastEditedByMaestro === true,
    lastEditedDate: str(r.lastEditedDate),
  };
}

export function groupFileRefOf(raw: unknown): GroupFileRef | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const conversationId = str(r.conversationId);
  const fileId = str(r.fileId);
  return conversationId && fileId ? { conversationId, fileId, ownerMetaSiteId: str(r.ownerMetaSiteId) } : null;
}

// ─── Gates ─────────────────────────────────────────────────────────────────────────────────────────────────────

/** "Share to group…" on a file: the feature is on and the row carries the file id the grant is made against. */
export function offerShareToGroup(file: { fileId?: string; isApp?: boolean }, groupChatEnabled: boolean): boolean {
  return groupChatEnabled && !file.isApp && typeof file.fileId === 'string' && file.fileId.length > 0;
}

/** Whether to show any edit affordance: the group may edit it, it is there, and it has text (an upload has none). */
export function canEditGroupFile(file: Pick<GroupFile, 'permission' | 'available' | 'referenceUrl'>): boolean {
  return file.permission === 'EDIT' && file.available && !file.referenceUrl;
}

export type UnavailableReason = 'revoked' | 'unavailable';
/** The refusals that draw the unavailable card instead of opening. */
export function unavailableReasonOf(refusal: string | null | undefined): UnavailableReason | null {
  if (refusal === 'FILE_REVOKED') return 'revoked';
  if (refusal === 'FILE_UNAVAILABLE') return 'unavailable';
  return null;
}
export function unavailableCopy(reason: UnavailableReason): { titleKey: string; bodyKey: string } {
  return reason === 'revoked'
    ? { titleKey: 'chat.group.files.revokedTitle', bodyKey: 'chat.group.error.fileRevoked' }
    : { titleKey: 'chat.group.files.unavailableTitle', bodyKey: 'chat.group.error.fileUnavailable' };
}

// ─── Share ─────────────────────────────────────────────────────────────────────────────────────────────────────

export interface ShareRequest {
  conversationId: string;
  fileId: string;
  /** Preselected EDIT in the dialog (D22). */
  permission: GroupFilePermission;
  /** Minted ONCE per share and reused on every retry, so a retry posts the share message once. */
  clientMessageId: string;
  text?: string;
}

export const DEFAULT_SHARE_PERMISSION: GroupFilePermission = 'EDIT';

/**
 * Whether the dialog's choices (permission, message) can still change. Not while sending, not once shared, and not
 * after "Shared, but the message didn't post": the grant already landed as chosen, and Retry resends the SAME
 * message under the same clientMessageId — so only Retry is left. Same rule on both phones.
 */
export function shareChoicesEditable(phase: { busy: boolean; notPosted: boolean; done: boolean }): boolean {
  return !phase.busy && !phase.notPosted && !phase.done;
}

export function shareBody(req: ShareRequest): Record<string, unknown> {
  const text = req.text?.trim();
  return { conversationId: req.conversationId, fileId: req.fileId, permission: req.permission, clientMessageId: req.clientMessageId, ...(text ? { text } : {}) };
}

export type ShareResult =
  | { kind: 'shared'; message: unknown }
  /** The grant landed, the message did not: retry with the SAME clientMessageId. */
  | { kind: 'messageNotPosted' }
  | { kind: 'error'; key: string; refusal: string | null };

export function interpretShare(outcome: GroupActionOutcome): ShareResult {
  if (outcome.kind === 'ok') {
    return outcome.data.messagePosted === true ? { kind: 'shared', message: outcome.data.message ?? null } : { kind: 'messageNotPosted' };
  }
  const refusal = outcome.kind === 'refused' ? outcome.refusal : null;
  return { kind: 'error', key: refusalKey(refusal), refusal };
}

// ─── Read / write ──────────────────────────────────────────────────────────────────────────────────────────────

export type ReadResult =
  | { kind: 'ready'; file: GroupFile; content: string; etag: string | null }
  | { kind: 'unavailable'; reason: UnavailableReason }
  | { kind: 'error'; key: string; refusal: string | null };

export function interpretRead(outcome: GroupActionOutcome): ReadResult {
  if (outcome.kind === 'ok') {
    const file = parseGroupFile(outcome.data.file);
    if (!file) return { kind: 'error', key: refusalKey(null), refusal: null };
    if (!file.available) return { kind: 'unavailable', reason: 'unavailable' };
    return { kind: 'ready', file, content: typeof outcome.data.content === 'string' ? outcome.data.content : '', etag: file.etag };
  }
  const refusal = outcome.kind === 'refused' ? outcome.refusal : null;
  const reason = unavailableReasonOf(refusal);
  return reason ? { kind: 'unavailable', reason } : { kind: 'error', key: refusalKey(refusal), refusal };
}

export type WriteResult =
  | { kind: 'saved'; file: GroupFile | null }
  /** Someone changed it since it was opened. NOTHING was written; the draft stays with the reader. */
  | { kind: 'conflict'; currentEtag: string | null; draft: string }
  /** The owner narrowed it to read-only underneath the editor. */
  | { kind: 'readOnly' }
  | { kind: 'unavailable'; reason: UnavailableReason }
  | { kind: 'error'; key: string };

export function interpretWrite(outcome: GroupActionOutcome, draft: string): WriteResult {
  if (outcome.kind === 'ok') return { kind: 'saved', file: parseGroupFile(outcome.data.file) };
  const refusal = outcome.kind === 'refused' ? outcome.refusal : null;
  if (refusal === 'FILE_EDIT_CONFLICT') {
    const etag = outcome.kind === 'refused' ? str(outcome.data.currentEtag) : null;
    return { kind: 'conflict', currentEtag: etag, draft };
  }
  if (refusal === 'FILE_READ_ONLY') return { kind: 'readOnly' };
  const reason = unavailableReasonOf(refusal);
  if (reason) return { kind: 'unavailable', reason };
  return { kind: 'error', key: refusalKey(refusal) };
}

/** The write body: never without the etag the editor opened (the gateway refuses a missing one). */
export function writeBody(file: Pick<GroupFile, 'conversationId' | 'fileId'>, content: string, expectedEtag: string): Record<string, unknown> {
  return { conversationId: file.conversationId, fileId: file.fileId, content, expectedEtag };
}

// ─── Lists ─────────────────────────────────────────────────────────────────────────────────────────────────────

/** A group the viewer can share into: an ACTIVE Symphony group in their chats list. */
export interface GroupChoice {
  conversationId: string;
  name: string;
}
export function groupChoices(rows: readonly { id?: string; name?: string; title?: string; group?: { conversationId?: string; name?: string; active?: boolean } | null }[]): GroupChoice[] {
  const seen = new Set<string>();
  const out: GroupChoice[] = [];
  for (const row of rows) {
    const g = row.group;
    const id = g?.conversationId || row.id;
    if (!g || g.active === false || !id || seen.has(id)) continue;
    seen.add(id);
    out.push({ conversationId: id, name: g.name || row.name || row.title || '' });
  }
  return out;
}

/** A share's group name, from the chats the viewer still has; a group they LEFT reads as the fallback. */
export function groupNameFor(conversationId: string, choices: readonly GroupChoice[], fallback: string): string {
  return choices.find((c) => c.conversationId === conversationId)?.name || fallback;
}

/** Active shares first (newest share first, as the service orders them), then the revoked ones. */
export function orderShares(shares: readonly GroupFileShare[]): GroupFileShare[] {
  return [...shares.filter((s) => s.active), ...shares.filter((s) => !s.active)];
}

/**
 * A group file as the explorer's row shape. `content` is the HEAD: `contentTruncated` is forced so nothing that acts
 * on a body (open, edit, download) can take it for the file — every such action reads through `read-file`.
 */
export function groupFileAsWorkspaceFile(file: GroupFile): WorkspaceFile {
  return {
    name: file.fileName,
    isApp: false,
    content: file.contentHead,
    contentTruncated: true,
    ...(file.contentType ? { contentType: file.contentType } : {}),
    ...(file.sizeBytes !== null ? { sizeBytes: String(file.sizeBytes) } : {}),
    ...(file.updatedDate ? { updatedDate: file.updatedDate } : {}),
    ...(file.createdDate ? { createdDate: file.createdDate } : {}),
    ...(file.hint ? { hint: file.hint } : {}),
    ...(file.referenceUrl ? { referenceUrl: file.referenceUrl } : {}),
  };
}

/**
 * A list row's unavailable state: what this device LEARNED opening it (FILE_REVOKED / FILE_UNAVAILABLE), else the
 * row's own `available: false` — which names no reason, so the list says the generic "File unavailable" until an
 * open answers with the specific one. Same rule as iOS `GroupFilesViewRules.unavailable` / Android `rowUnavailable`.
 */
export function rowUnavailable(file: Pick<GroupFile, 'available'>, learned: UnavailableReason | null | undefined): UnavailableReason | null {
  return learned ?? (file.available ? null : 'unavailable');
}

/** The key a learned unavailable reason is remembered under (the card and the list rows share it). */
export const groupFileKey = (ref: { conversationId: string; fileId: string }) => `${ref.conversationId}|${ref.fileId}`;

/** "You" for the viewer's own file, "Shared by {name}", or nothing when the owner has no name to show. */
export function groupFileOwnerLine(file: Pick<GroupFile, 'mine' | 'ownerName'>, t: (key: string, params?: Record<string, unknown>) => string): string | null {
  if (file.mine) return t('chat.group.files.you');
  const name = file.ownerName.trim();
  return name ? t('chat.group.files.sharedBy', { name }) : null;
}

/** The row's caption: whose it is and what the group may do — or why it can no longer be opened. */
export function groupFileCaption(
  file: GroupFile,
  t: (key: string, params?: Record<string, unknown>) => string,
  learned?: UnavailableReason | null,
): string {
  // What an open learned wins; else the server's own reason (Phase 8), else the generic "File unavailable".
  if (learned) return t(unavailableCopy(learned).titleKey);
  if (!file.available) return t(unavailableReasonKey(file.unavailableReason));
  const permission = t(file.permission === 'EDIT' ? 'chat.group.files.canEdit' : 'chat.group.files.readOnly');
  const who = groupFileOwnerLine(file, t);
  return who ? `${who} · ${permission}` : permission;
}

// ─── "Shared with groups": who last edited ─────────────────────────────────────────────────────────────────────

/** Who a share's editor is, as this device knows the group: its participants, and which one is the viewer. */
export interface GroupPeople {
  viewerId?: string | null;
  participants: readonly { id: string; kind: string; displayName: string }[];
}

/**
 * "Last edited by {name}" / "Last edited by {name}'s Maestro" / "Last edited by your Maestro", then " · {date}" when
 * the share carries one. The viewer's own edit reads "You"; anyone else by FIRST name from the group's members. Null
 * when nobody edited it or the editor can't be named (a group the owner LEFT is not in their list). Same line on iOS
 * `GroupFiles.lastEditedLine` and Android `GroupFilesWire.lastEditedLine`.
 */
export function lastEditedLine(
  share: Pick<GroupFileShare, 'lastEditedByMetaSiteId' | 'lastEditedByMaestro' | 'lastEditedDate'>,
  people: GroupPeople | null,
  t: (key: string, params?: Record<string, unknown>) => string,
  formatDate: (iso: string) => string,
): string | null {
  const id = share.lastEditedByMetaSiteId;
  if (!id) return null;
  let line: string | null = null;
  if (people?.viewerId && id === people.viewerId) {
    line = share.lastEditedByMaestro ? t('chat.group.files.lastEditedByYourMaestro') : t('chat.group.files.lastEditedBy', { name: t('chat.group.files.you') });
  } else {
    const person = people?.participants.find((p) => p.id === id && p.kind === 'HUMAN');
    const name = person ? (person.displayName.trim().split(/\s+/)[0] ?? '') : '';
    if (name) line = t(share.lastEditedByMaestro ? 'chat.group.files.lastEditedByMaestro' : 'chat.group.files.lastEditedBy', { name });
  }
  if (!line) return null;
  const when = share.lastEditedDate ? formatDate(share.lastEditedDate) : '';
  return when ? `${line} · ${when}` : line;
}

// ─── The editor's kept draft ───────────────────────────────────────────────────────────────────────────────────

/**
 * The user's own edits, kept after a Save that could not land: the owner narrowed the file to read-only underneath
 * the editor (FILE_READ_ONLY), or someone else changed it and the reader reloaded their version (FILE_EDIT_CONFLICT →
 * Reload). Either way the editor is closed, the draft is NEVER discarded, and one row offers "Copy my edits".
 */
export interface KeptDraft {
  reason: 'readOnly' | 'conflict';
  text: string;
}
export function keptDraftLabelKey(kept: KeptDraft): string {
  return kept.reason === 'readOnly' ? 'chat.group.error.fileReadOnly' : 'chat.group.files.conflictTitle';
}
